<template>
  <div class="regional-map-page">
    <AppNav />

    <div class="page-header">
      <h2>
        <i class="fas fa-map"></i>
        区域人数地图
      </h2>
      <p class="subtitle">在平面图上查看各区域实时人数；可在「编辑地图」中上传底图并标记多边形区域</p>
    </div>

    <SessionGroupTabs
      v-if="sessionsList.length"
      :value="activeTab"
      :sessions="sessionsList"
      @change="onSessionChange"
    />

    <div class="mode-tabs">
      <button
        type="button"
        :class="['mode-btn', { active: viewMode === 'view' }]"
        @click="viewMode = 'view'"
      >
        查看人数
      </button>
      <button
        type="button"
        :class="['mode-btn', { active: viewMode === 'edit' }]"
        @click="viewMode = 'edit'"
      >
        编辑地图
      </button>
    </div>

    <VenueMapEditor
      v-if="viewMode === 'edit'"
      :layout="layout"
      :stages="stages"
      :known-areas="knownAreas"
      @update:layout="layout = $event"
      @saved="loadLayout"
    />

    <template v-else>
      <div v-if="!hasLayout" class="empty-layout">
        <p>当前场次尚未配置场地地图。</p>
        <button type="button" class="btn primary" @click="viewMode = 'edit'">去上传并标记区域</button>
      </div>

      <template v-else>
        <div class="filters">
          <div class="filter-row" v-if="dates.length">
            <span class="filter-label">日期</span>
            <div class="date-chips">
              <button
                v-for="d in dates"
                :key="d"
                type="button"
                class="date-chip"
                :class="{ active: selectedDate === d }"
                @click="selectDate(d)"
              >
                {{ formatDateLabel(d) }}
              </button>
            </div>
            <label class="custom-time-inline">
              <span>自定义</span>
              <input
                type="time"
                v-model="selectedTime"
                :disabled="!selectedDate"
                @change="onCustomTimeChange"
              />
            </label>
            <button type="button" class="btn btn-sm" @click="fetchSnapshot" :disabled="loadingSnapshot">
              {{ loadingSnapshot ? '…' : '刷新' }}
            </button>
          </div>
          <p v-else class="no-time-hint">当前场次暂无演出日期</p>

          <div v-if="timeOptionsByStage.length" class="stage-time-groups">
            <div
              v-for="stageGroup in timeOptionsByStage"
              :key="stageGroup.stage"
              class="stage-time-row"
            >
              <span
                class="stage-time-title"
                :style="{ borderLeftColor: getStageColor(stageGroup.stage) }"
              >
                {{ stageGroup.stage }}
              </span>
              <div class="time-chips">
                <button
                  v-for="opt in stageGroup.options"
                  :key="opt.key"
                  type="button"
                  class="time-chip"
                  :class="{ active: activeTimeKey === opt.key }"
                  @click="selectArtistTime(opt)"
                >
                  <span class="chip-time">{{ opt.time }}</span>
                  <span class="chip-artist">{{ opt.label }}</span>
                </button>
              </div>
            </div>
          </div>
          <p v-else-if="selectedDate && dates.length" class="no-time-hint">该日期暂无艺人演出</p>
        </div>

        <div class="map-layout">
          <div class="map-main">
            <VenueMapCanvas
              :layout="layout"
              :counts="counts"
              :selected-region-ids="selectedRegionIds"
              :show-counts="true"
              @region-click="onRegionClick"
            />
            <p class="map-hint">点击区域多选，再次点击取消选择</p>
            <div class="legend">
              <span>人数少</span>
              <span class="legend-bar"></span>
              <span>人数多</span>
              <span v-if="targetTime" class="snapshot-time">查询时刻：{{ formatDateTime(targetTime) }}</span>
            </div>
          </div>

          <aside class="side-panel" v-if="selectedRegionIds.length">
            <div class="panel-header">
              <div class="panel-title">
                <h3>已选 {{ selectedRegionIds.length }} 个区域</h3>
                <span v-if="totalSelectedCount != null" class="count-total">{{ totalSelectedCount }} 人</span>
              </div>
              <button type="button" class="btn-link" @click="clearSelectedRegions">清空</button>
            </div>

            <template v-if="focusRegion">
              <div class="focus-bar">
                <span class="focus-name">{{ getRegionDisplayName(focusRegion) }}</span>
                <span class="focus-count">
                  {{ focusRegionCount != null ? `${focusRegionCount} 人` : '暂无数据' }}
                </span>
              </div>
              <div class="image-viewer">
                <p v-if="focusImageTimeLabel" class="image-time">{{ focusImageTimeLabel }}</p>
                <img
                  v-if="focusRawImageUrl"
                  :src="focusRawImageUrl"
                  class="region-image clickable"
                  title="点击放大查看"
                  @click="openImagePreview"
                />
                <div v-else class="no-image">该时刻附近暂无原始图片</div>
              </div>
            </template>

            <ul class="selected-regions-list">
              <li
                v-for="region in selectedRegions"
                :key="region.id"
                :class="{ active: focusRegionId === region.id }"
                @click="focusRegionId = region.id"
              >
                <span class="region-name">{{ getRegionDisplayName(region) }}</span>
                <span class="region-count">{{ getRegionCount(region.id) ?? '—' }}人</span>
              </li>
            </ul>

            <MultiAreaStackChart
              v-if="chartStart && chartEnd && chartRegions.length"
              :regions="chartRegions"
              :start-time="chartStart"
              :end-time="chartEnd"
              :artists="selectedDateArtists"
              :visible-stages="visibleChartStages"
            />
          </aside>
        </div>
      </template>
    </template>

    <div v-if="showImagePreview" class="image-preview-overlay" @click="closeImagePreview">
      <div class="image-preview-container" @click.stop>
        <div class="preview-header">
          <span>{{ focusRegion ? getRegionDisplayName(focusRegion) : '' }}</span>
          <span v-if="focusImageTimeLabel" class="preview-sub">{{ focusImageTimeLabel }}</span>
        </div>
        <img
          :src="focusRawImageUrl"
          class="preview-image"
          :style="{ transform: `scale(${previewScale})` }"
        />
        <div class="preview-controls">
          <button type="button" @click="zoomIn" title="放大">+</button>
          <button type="button" @click="zoomOut" title="缩小">−</button>
          <button type="button" @click="closeImagePreview" title="关闭">✕</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../api/config';
import { resetStageColors, getStageColor } from '../utils/stageColors';
import { getRegionDisplayName } from '../utils/venueMap';
import AppNav from './AppNav.vue';
import SessionGroupTabs from './SessionGroupTabs.vue';
import { resolveDefaultSessionId } from '../utils/sessions';
import VenueMapCanvas from './VenueMapCanvas.vue';
import VenueMapEditor from './VenueMapEditor.vue';
import MultiAreaStackChart from './MultiAreaStackChart.vue';

export default {
  name: 'RegionalMapPage',
  components: {
    AppNav,
    SessionGroupTabs,
    VenueMapCanvas,
    VenueMapEditor,
    MultiAreaStackChart,
  },
  data() {
    return {
      sessionsList: [],
      activeTab: localStorage.getItem('sessionId') || '',
      viewMode: 'view',
      layout: {
        version: 1,
        imageWidth: 1920,
        imageHeight: 1080,
        regions: [],
      },
      stages: [],
      knownAreas: [],
      dates: [],
      artistsByDate: [],
      selectedDate: '',
      selectedTime: '',
      activeTimeKey: '',
      selectedStage: 'all',
      counts: {},
      images: {},
      targetTime: '',
      loadingSnapshot: false,
      selectedRegionIds: [],
      focusRegionId: null,
      chartStart: '',
      chartEnd: '',
      imageBaseUrl: 'https://crowd-counting.chunjuan.fun/MFfile',
      showImagePreview: false,
      previewScale: 1,
    };
  },
  computed: {
    hasLayout() {
      return this.layout.regions?.length > 0;
    },
    selectedRegions() {
      return this.layout.regions.filter(r => this.selectedRegionIds.includes(r.id));
    },
    chartRegions() {
      return this.selectedRegions.map(r => ({
        id: r.id,
        label: getRegionDisplayName(r),
      }));
    },
    focusRegion() {
      if (!this.focusRegionId) return null;
      return this.layout.regions.find(r => r.id === this.focusRegionId) || null;
    },
    totalSelectedCount() {
      if (!this.selectedRegionIds.length) return null;
      let total = 0;
      let hasData = false;
      this.selectedRegionIds.forEach(id => {
        const count = this.getRegionCount(id);
        if (count != null) {
          total += count;
          hasData = true;
        }
      });
      return hasData ? total : null;
    },
    focusRegionCount() {
      if (!this.focusRegionId) return null;
      return this.getRegionCount(this.focusRegionId);
    },
    focusRawImageUrl() {
      if (!this.focusRegionId) return '';
      const img = this.images[this.focusRegionId];
      const raw = img?.raw_image_path;
      if (!raw) return '';
      return `${this.imageBaseUrl}/${raw}`;
    },
    focusImageTimeLabel() {
      if (!this.focusRegionId) return '';
      const img = this.images[this.focusRegionId];
      if (!img?.matched_datetime) return '';
      const shot = this.formatDateTime(img.matched_datetime);
      const target = this.targetTime ? this.formatDateTime(this.targetTime) : '';
      const diff = img.time_diff_seconds;
      const diffText = diff != null ? `（相差 ${diff} 秒）` : '';
      if (target) {
        return `已匹配最接近 ${target} 的图像，拍摄于 ${shot}${diffText}`;
      }
      return `拍摄于 ${shot}${diffText}`;
    },
    visibleChartStages() {
      const fromRegions = [...new Set(
        this.selectedRegions.map(r => r.stage).filter(Boolean)
      )];
      if (fromRegions.length) return fromRegions;
      return this.timeOptionsByStage.map(g => g.stage);
    },
    selectedDateArtists() {
      const group = this.artistsByDate.find(d => d.date === this.selectedDate);
      return group?.artists || [];
    },
    timeOptionsByStage() {
      const group = this.artistsByDate.find(d => d.date === this.selectedDate);
      if (!group?.artists?.length) return [];

      const byStage = {};
      group.artists
        .filter(artist => artist.startTime)
        .forEach(artist => {
          const stage = artist.stage || '未分类';
          const time = this.addMinutesToTime(artist.startTime, 10);
          if (!byStage[stage]) byStage[stage] = [];
          byStage[stage].push({
            key: `${artist.name}|${artist.stage}|${time}`,
            time,
            label: artist.name,
            stage: artist.stage,
          });
        });

      Object.values(byStage).forEach(options =>
        options.sort((a, b) => a.time.localeCompare(b.time) || a.label.localeCompare(b.label))
      );

      const stageOrder = this.stages.map(s => s.id);
      return Object.keys(byStage)
        .sort((a, b) => {
          const ia = stageOrder.indexOf(a);
          const ib = stageOrder.indexOf(b);
          if (ia >= 0 && ib >= 0) return ia - ib;
          if (ia >= 0) return -1;
          if (ib >= 0) return 1;
          return a.localeCompare(b);
        })
        .map(stage => ({ stage, options: byStage[stage] }));
    },
    timeOptions() {
      return this.timeOptionsByStage.flatMap(g => g.options);
    },
  },
  async created() {
    await this.loadSessions();
    await Promise.all([
      this.loadLayout(),
      this.fetchStagesAndAreas(),
      this.fetchArtists(),
    ]);
    this.syncSelectedDate();
    this.applyDefaultTime();
    this.updateChartRange();
    await this.fetchSnapshot();
  },
  mounted() {
    document.addEventListener('keydown', this.handlePreviewKeydown);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handlePreviewKeydown);
  },
  methods: {
    async loadSessions() {
      try {
        const res = await api.get('/sessions');
        this.sessionsList = res.data.sessions || [];
        const storedSessionId = localStorage.getItem('sessionId');
        this.activeTab = resolveDefaultSessionId(this.sessionsList, storedSessionId);
        if (this.activeTab) {
          localStorage.setItem('sessionId', this.activeTab);
        }
      } catch (e) {
        console.error(e);
      }
    },
    onSessionChange(id) {
      this.activeTab = id;
      localStorage.setItem('sessionId', id);
      resetStageColors();
      this.loadLayout();
      this.fetchStagesAndAreas();
      this.selectedRegionIds = [];
      this.focusRegionId = null;
      this.fetchArtists().then(() => {
        this.syncSelectedDate();
        this.applyDefaultTime();
        this.updateChartRange();
        this.fetchSnapshot();
      });
    },
    async loadLayout() {
      try {
        const res = await api.get('/venue-layout');
        this.layout = res.data.layout || this.layout;
      } catch (e) {
        console.error(e);
      }
    },
    async fetchStagesAndAreas() {
      try {
        const [stagesRes, areasRes] = await Promise.all([
          api.get('/stages'),
          api.get('/areas'),
        ]);
        this.stages = stagesRes.data.stages || [];
        this.knownAreas = areasRes.data.areas || [];
      } catch (e) {
        console.error(e);
      }
    },
    async fetchArtists() {
      try {
        const res = await api.get('/artists/by-date');
        this.artistsByDate = (res.data.artistsByDate || [])
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        this.dates = this.artistsByDate.map(group => group.date);
      } catch (e) {
        console.error(e);
        this.artistsByDate = [];
        this.dates = [];
      }
    },
    syncSelectedDate() {
      if (!this.dates.length) {
        this.selectedDate = '';
        return;
      }
      if (!this.dates.includes(this.selectedDate)) {
        this.selectedDate = this.dates[0];
      }
    },
    applyDefaultTime() {
      if (this.timeOptions.length) {
        const first = this.timeOptions[0];
        this.selectedTime = first.time;
        this.activeTimeKey = first.key;
        return;
      }
      if (!this.selectedTime) {
        this.selectedTime = '14:00';
      }
      this.activeTimeKey = 'custom';
    },
    onDateChange() {
      this.applyDefaultTime();
      this.updateChartRange();
      this.fetchSnapshot();
    },
    selectDate(date) {
      if (this.selectedDate === date) return;
      this.selectedDate = date;
      this.onDateChange();
    },
    selectArtistTime(opt) {
      this.selectedTime = opt.time;
      this.activeTimeKey = opt.key;
      this.fetchSnapshot();
    },
    /** HH:mm / HH:mm:ss → 加 minutes 分钟后的 HH:mm */
    addMinutesToTime(timeStr, minutes) {
      const [h = 0, m = 0] = String(timeStr).split(':').map(Number);
      const total = ((h * 60 + m + minutes) % (24 * 60) + 24 * 60) % (24 * 60);
      const hh = String(Math.floor(total / 60)).padStart(2, '0');
      const mm = String(total % 60).padStart(2, '0');
      return `${hh}:${mm}`;
    },
    onCustomTimeChange() {
      this.activeTimeKey = 'custom';
      this.fetchSnapshot();
    },
    formatDateLabel(dateStr) {
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) return dateStr;
      return `${date.getMonth() + 1}月${date.getDate()}日`;
    },
    updateChartRange() {
      if (!this.selectedDate) return;
      this.chartStart = `${this.selectedDate}T00:00`;
      this.chartEnd = `${this.selectedDate}T23:59`;
    },
    async fetchSnapshot() {
      if (!this.selectedDate) return;
      this.loadingSnapshot = true;
      try {
        const res = await api.get('/areas/snapshot', {
          params: {
            date: this.selectedDate,
            time: this.selectedTime,
            stage: this.selectedStage,
          },
        });
        this.counts = res.data.counts || {};
        this.images = res.data.images || {};
        this.targetTime = res.data.targetTime || '';

      } catch (e) {
        console.error(e);
        this.counts = {};
        this.images = {};
        this.targetTime = '';
      } finally {
        this.loadingSnapshot = false;
      }
    },
    getRegionDisplayName,
    getStageColor,
    getRegionCount(regionId) {
      const img = this.images[regionId];
      if (img?.area_count != null) return img.area_count;
      const c = this.counts[regionId];
      return c != null ? c : null;
    },
    onRegionClick(region) {
      const ids = [...this.selectedRegionIds];
      const idx = ids.indexOf(region.id);
      if (idx >= 0) {
        ids.splice(idx, 1);
        if (this.focusRegionId === region.id) {
          this.focusRegionId = ids.length ? ids[ids.length - 1] : null;
        }
      } else {
        ids.push(region.id);
        this.focusRegionId = region.id;
      }
      this.selectedRegionIds = ids;
      this.updateChartRange();
    },
    clearSelectedRegions() {
      this.selectedRegionIds = [];
      this.focusRegionId = null;
      this.closeImagePreview();
    },
    openImagePreview() {
      if (!this.focusRawImageUrl) return;
      this.showImagePreview = true;
      this.previewScale = 1;
    },
    closeImagePreview() {
      this.showImagePreview = false;
      this.previewScale = 1;
    },
    zoomIn() {
      this.previewScale = Math.min(this.previewScale + 0.25, 4);
    },
    zoomOut() {
      this.previewScale = Math.max(this.previewScale - 0.25, 0.5);
    },
    handlePreviewKeydown(event) {
      if (!this.showImagePreview) return;
      if (event.key === 'Escape') this.closeImagePreview();
      else if (event.key === '+' || event.key === '=') this.zoomIn();
      else if (event.key === '-' || event.key === '_') this.zoomOut();
    },
    formatDateTime(value) {
      if (!value) return '';
      const normalized = String(value).replace('T', ' ').replace('.000Z', '').replace('Z', '');
      const [datePart, timePart = ''] = normalized.split(' ');
      const time = timePart.slice(0, 8);
      return time ? `${datePart} ${time}` : datePart;
    },
  },
  watch: {
    selectedDate(val) {
      if (val) {
        this.chartStart = `${val}T00:00`;
        this.chartEnd = `${val}T23:59`;
      }
    },
  },
};
</script>

<style scoped>
.regional-map-page {
  max-width: 1400px;
  margin: 0 auto;
}

.page-header h2 {
  margin: 0 0 4px;
  font-size: 1.25rem;
}

.subtitle {
  margin: 0 0 10px;
  color: #666;
  font-size: 13px;
}

.mode-tabs {
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
}

.mode-btn {
  padding: 5px 14px;
  border: 1px solid #d9d9d9;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}

.mode-btn.active {
  background: #1677ff;
  color: #fff;
  border-color: #1677ff;
}

.filters {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 10px;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.date-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
}

.date-chip {
  padding: 4px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.15s;
  line-height: 1.4;
}

.date-chip:hover {
  border-color: #1677ff;
  color: #1677ff;
}

.date-chip.active {
  background: #1677ff;
  border-color: #1677ff;
  color: #fff;
  font-weight: 600;
}

.custom-time-inline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #666;
  white-space: nowrap;
}

.custom-time-inline input {
  padding: 3px 6px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 12px;
  width: 96px;
}

.custom-time-inline input:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.btn-sm {
  padding: 4px 10px;
  font-size: 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  white-space: nowrap;
}

.btn-sm:hover:not(:disabled) {
  border-color: #1677ff;
  color: #1677ff;
}

.btn-sm:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.stage-time-groups {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.stage-time-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.stage-time-title {
  font-size: 12px;
  font-weight: 600;
  color: #333;
  padding: 2px 0 2px 6px;
  border-left: 3px solid #1677ff;
  white-space: nowrap;
  min-width: 48px;
}

.filter-label {
  font-size: 12px;
  color: #666;
  font-weight: 600;
  white-space: nowrap;
}

.time-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
}

.time-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s;
  max-width: none;
}

.time-chip:hover {
  border-color: #1677ff;
  color: #1677ff;
}

.time-chip.active {
  background: #e6f4ff;
  border-color: #1677ff;
  color: #1677ff;
}

.chip-time {
  font-weight: 600;
  font-size: 12px;
}

.chip-artist {
  font-size: 12px;
  color: #666;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.time-chip.active .chip-artist {
  color: #1677ff;
}

.no-time-hint {
  font-size: 12px;
  color: #999;
  margin: 0;
}

.map-layout {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.map-main {
  flex: 1;
  min-width: 0;
}

.map-hint {
  margin: 4px 0 0;
  font-size: 11px;
  color: #999;
}

.side-panel {
  width: 380px;
  flex-shrink: 0;
  background: #fff;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.panel-title {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}

.panel-header h3 {
  margin: 0;
  font-size: 0.95rem;
  white-space: nowrap;
}

.count-total {
  font-size: 13px;
  font-weight: 600;
  color: #1677ff;
  white-space: nowrap;
}

.btn-link {
  border: none;
  background: none;
  color: #1677ff;
  cursor: pointer;
  font-size: 13px;
  padding: 0;
}

.btn-link:hover {
  text-decoration: underline;
}

.selected-regions-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 88px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
}

.selected-regions-list li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 10px;
  cursor: pointer;
  font-size: 12px;
  border-bottom: 1px solid #f5f5f5;
}

.selected-regions-list li:last-child {
  border-bottom: none;
}

.selected-regions-list li:hover {
  background: #fafafa;
}

.selected-regions-list li.active {
  background: #e6f4ff;
}

.region-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 8px;
}

.region-count {
  color: #1677ff;
  font-weight: 600;
  flex-shrink: 0;
}

.focus-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}

.focus-name {
  color: #333;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.focus-count {
  color: #1677ff;
  font-weight: 600;
  flex-shrink: 0;
}

.image-viewer {
  background: #f9fafb;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 6px 8px;
}

.image-time {
  margin: 0 0 4px;
  color: #666;
  font-size: 11px;
  line-height: 1.4;
}

.region-image {
  width: 100%;
  max-height: 160px;
  object-fit: contain;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #eee;
  display: block;
}

.region-image.clickable {
  cursor: zoom-in;
}

.region-image.clickable:hover {
  border-color: #1677ff;
  box-shadow: 0 0 0 1px rgba(22, 119, 255, 0.2);
}

.image-preview-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.82);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 24px;
}

.image-preview-container {
  position: relative;
  max-width: 95vw;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: auto;
}

.preview-header {
  color: #fff;
  text-align: center;
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 600;
}

.preview-sub {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.75);
}

.preview-image {
  max-width: 90vw;
  max-height: 80vh;
  object-fit: contain;
  transition: transform 0.2s ease;
  border-radius: 4px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
}

.preview-controls {
  position: fixed;
  top: 20px;
  right: 20px;
  display: flex;
  gap: 8px;
  z-index: 2001;
}

.preview-controls button {
  min-width: 40px;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.95);
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
}

.preview-controls button:last-child {
  background: #ff6b6b;
  color: #fff;
}

.preview-controls button:hover {
  opacity: 0.9;
}

.no-image {
  color: #999;
  font-size: 12px;
  text-align: center;
  padding: 10px 0;
}

.no-data {
  color: #999;
}

.legend {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 12px;
  color: #666;
}

.legend-bar {
  width: 120px;
  height: 10px;
  border-radius: 4px;
  background: linear-gradient(90deg, rgba(76,175,80,0.8), rgba(255,193,7,0.8), rgba(244,67,54,0.8));
}

.snapshot-time {
  margin-left: auto;
}

.empty-layout {
  text-align: center;
  padding: 48px;
  background: #fff;
  border-radius: 8px;
}

.btn.primary {
  padding: 10px 20px;
  background: #1677ff;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 12px;
}
</style>
